import { useEffect, useRef, useState } from "react";
import { Button, Col, Form, Row, Select, FormInstance } from "antd";
import { DateSelectionType, type DateValue, CompanyIdentificationType, CapitalizationActualsPlaceholderName, CurrencySelectionType } from "@/types/newLinkBuilder";
import { DialogMessageType } from "@/types/commands";
import { buildFormula, FormulaType } from "@/utils/formulaBuilder";
import {
  DATA_SCALE_OPTIONS,
  type DataScaleOption,
} from "@/state/actuals/commonEquityStore";
import Ring from "@/components/ui/Ring";
import PageTitle from "@/components/ui/PageTitle";
import CompanyLogo from "@/components/ui/CompanyLogo";
import CompanyIdentificationForm from "../CompanyIdentificationForm";
import FormulaBuilderForm from "../FormulaBuilderForm";
import DataSourceForm from "../DataSourceForm";
import DataTypeForm from "../DataTypeForm";
import { validateForm } from "@/utils/validation";
import { usePreferredEquityStore } from "@/state/actuals/preferredEquityStore";
import { dataTypeTreeDataForPreferredEquity } from "./dataTypeTreeData";
import DataCurrencyForm from "../DataCurrencyForm";

const PreferredEquity: React.FC = () => {
  const [form] = Form.useForm();
  const {
    companyIdentification,
    setCompanyIdentification,
    dataSource,
    setDataSource,
    asOfDate,
    setAsOfDate,
    dataType,
    setDataType,
    dataScale,
    setDataScale,
    isScaleUsed,
    formula,
    setFormula,
    formulaLocation,
    setFormulaLocation,
    currency,
    setCurrency,
    isCurrencyUsed,
  } = usePreferredEquityStore((state) => state);

  const companyIdentificationRef = useRef<HTMLDivElement>(null);
  const formulaBuilderRef = useRef<HTMLDivElement>(null);
  const [scrollTarget, setScrollTarget] = useState<'company' | 'formula' | null>(null);

  useEffect(() => {
    const dateValue: DateValue = {
      selected: DateSelectionType.Date,
      date: asOfDate?.toISOString(),
    };
    buildFormula(FormulaType.CapitalizationActuals, {
      companyIdentification,
      dataType,
      dataSource,
      date: dateValue,
      dataScale,
      isScaleUsed,
      isCurrencyUsed,
      currency: {
        selected: CurrencySelectionType.Value,
        currency: currency,
      },
    }).then(setFormula);
  }, [companyIdentification, dataType, dataSource, asOfDate, dataScale, currency, isCurrencyUsed, setFormula]);

  useEffect(() => {
    if (!scrollTarget) return;
    const ref = scrollTarget === 'company' ? companyIdentificationRef : formulaBuilderRef;
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setScrollTarget(null);
  }, [scrollTarget]);

  useEffect(() => {
    const placeholder = localStorage.getItem("placeholder");
    const cellAddress = localStorage.getItem("cellAddress");
    const activeCell = localStorage.getItem("activeCell");

    if (activeCell) setFormulaLocation(activeCell);

    if (placeholder) {
      const placeholderData = JSON.parse(placeholder);
      const { companyIdentification: placeholderCI, formulaLocation: placeholderFL } = placeholderData.data || {};

      if (placeholderCI) setCompanyIdentification(placeholderCI);
      if (placeholderFL) setFormulaLocation(placeholderFL);

      const placeholderHandlers: Record<string, () => void> = {
        [CapitalizationActualsPlaceholderName.CompanyIdentificationExchangeCellReference]: () => {
          setCompanyIdentification(prev => ({
            ...prev,
            selected: CompanyIdentificationType.CellReference,
            exchange: cellAddress || "",
          }));
          setScrollTarget('company');
        },
        [CapitalizationActualsPlaceholderName.CompanyIdentificationTickerCellReference]: () => {
          setCompanyIdentification(prev => ({
            ...prev,
            selected: CompanyIdentificationType.CellReference,
            ticker: cellAddress || "",
          }));
          setScrollTarget('company');
        },
        [CapitalizationActualsPlaceholderName.FormulaLocationCellReference]: () => {
          setFormulaLocation(cellAddress || "");
          setScrollTarget('formula');
        },
      };

      placeholderHandlers[placeholderData.placeholderName]?.();
    }

    return () => {
      localStorage.removeItem("placeholder");
      localStorage.removeItem("cellAddress");
    };
  }, [setCompanyIdentification, setFormulaLocation]);

  const handlePlaceholder = (name: CapitalizationActualsPlaceholderName) => {
    localStorage.setItem("placeholder", JSON.stringify({
      placeholderName: name,
      data: { companyIdentification, formulaLocation },
    }));
    localStorage.setItem("redirectTo", "actuals");
    Office.context.ui.messageParent(JSON.stringify({ action: DialogMessageType.OPEN_PLACEHOLDER }));
  };

  const handleOk = (form: FormInstance) => {
    const validations = [
      { value: companyIdentification, field: "companyIdentification" },
      { value: dataSource, field: "dataSource" },
      { value: asOfDate, field: "asOfDateActuals" },
      { value: dataType, field: "dataType" },
      { value: dataScale, field: "dataScale" },
      { value: formulaLocation, field: "formulaLocation" },
    ];

    const hasError = validations.some(({ value, field }) => validateForm(value, form, field));

    if (!hasError) {
      Office.context.ui.messageParent(JSON.stringify({
        action: DialogMessageType.ADD_LINK_TO_CELL,
        data: { formula, address: formulaLocation },
      }));
    }
  };

  const handleCancel = () => {
    Office.context.ui.messageParent(JSON.stringify({ action: DialogMessageType.CLOSE }));
  };

  return (
    <Form form={form}>
      <Ring className="p-4 gap-8 flex flex-col">
        <div className="flex flex-col items-between gap-4 w-full">
          <div className="flex items-center justify-between gap-2">
            <PageTitle className="!mb-0 flex items-center gap-2 !text-2xl !font-bold">Select Company</PageTitle>
            <CompanyLogo domain="morganstanley.com" height={20} />
          </div>
          <div ref={companyIdentificationRef} className="flex w-3/5 flex-col gap-2">
            <CompanyIdentificationForm value={companyIdentification} setValue={setCompanyIdentification} handlePlaceholder={handlePlaceholder} />
          </div>
        </div>
        <div className="flex flex-col items-between gap-4 w-full">
          <PageTitle className="!mb-0 flex items-center gap-2 !text-2xl !font-bold">Select Data</PageTitle>
          <DataSourceForm dataSource={dataSource} onDataSourceChange={setDataSource} asOfDate={asOfDate} onAsOfDateChange={setAsOfDate} />
          <DataTypeForm dataType={dataType} onDataTypeChange={setDataType} dataTypeTreeData={dataTypeTreeDataForPreferredEquity} />
          {isScaleUsed && (
            <Form.Item name="dataScale" className="!mb-2">
              <Row align="middle" gutter={16}>
                <Col span={12}>
                  <Row align="middle">
                    <Col span={8} style={{ display: "flex", justifyContent: "flex-start" }}>
                      <label htmlFor="dataScale">Data Scale:</label>
                    </Col>
                    <Col span={16} style={{ display: "flex", justifyContent: "flex-start" }}>
                      <Select<DataScaleOption>
                        id="dataScale"
                        value={dataScale}
                        onChange={setDataScale}
                        style={{ width: "70%" }}
                        options={DATA_SCALE_OPTIONS}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Form.Item>
          )}
          {isCurrencyUsed && (
            <DataCurrencyForm currency={currency} onCurrencyChange={setCurrency} />
          )}
        </div>

        <div ref={formulaBuilderRef} className="flex w-full flex-col gap-4">
          <PageTitle className="!mb-0 flex items-center gap-2 !text-2xl !font-bold">Formula Builder</PageTitle>
          <FormulaBuilderForm formula={formula} formulaLocation={formulaLocation} setFormulaLocation={setFormulaLocation} handlePlaceholder={handlePlaceholder} />
        </div> 
      </Ring>
      <div className="flex justify-end items-center gap-2.5 my-5">
          <Button type="primary" onClick={() => handleOk(form)}>Ok</Button>
          <Button onClick={handleCancel}>Cancel</Button>
        </div>
    </Form>
  );
};

export default PreferredEquity;
